import React, { useState, useRef, useEffect } from 'react';
import { monthNames, navigateMonth, formatMonthLabel, formatPeriodLabel } from '../utils/formatters';

export type PeriodMode = 'month' | 'dateRange';

export interface PeriodFilter {
  mode: PeriodMode;
  months: string[];          // YYYY-MM[] (month mode)
  startDate: string;         // YYYY-MM-DD (dateRange mode)
  endDate: string;           // YYYY-MM-DD (dateRange mode)
}

interface PeriodSelectorProps {
  value: PeriodFilter;
  onChange: (filter: PeriodFilter) => void;
  singleMonth?: boolean;     // FinancialDashboard/BankAccounts: single month only
  showAllMonths?: boolean;   // Show "Todos os Meses" button (default true)
  onRefresh?: () => void;    // "Atualizar" button callback
  /**
   * Recarga em andamento. Sem isto o botão "Atualizar" busca de verdade mas não
   * dá sinal nenhum: quando a lista não muda visivelmente (02/09/2026, emissão
   * fiscal do mês) o operador conclui que travou e clica de novo. Passe o mesmo
   * estado de loading que o `onRefresh` liga.
   */
  refreshing?: boolean;
}

const currentMonthStr = (() => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
})();

export const defaultPeriodFilter = (months?: string[]): PeriodFilter => ({
  mode: 'month',
  months: months ?? [currentMonthStr],
  startDate: '',
  endDate: '',
});

export default function PeriodSelector({ value, onChange, singleMonth = false, showAllMonths = true, onRefresh, refreshing = false }: PeriodSelectorProps) {
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  // Carimbo da última recarga concluída: marcado na transição refreshing true→false.
  // Só assim o "Atualizado HH:MM:SS" prova que a busca terminou, em vez de
  // apenas registrar o clique.
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const wasRefreshing = useRef(false);
  useEffect(() => {
    if (wasRefreshing.current && !refreshing) {
      // Fuso pinado em BRT, como o resto do app (ver fmtDateTime do
      // DisputeRankingDrawer): o carimbo é comparado com horário de portal e de
      // log, e um relógio de outro fuso faria a tela mentir sem avisar.
      setRefreshedAt(
        new Date().toLocaleTimeString('pt-BR', { hour12: false, timeZone: 'America/Sao_Paulo' }),
      );
    }
    wasRefreshing.current = refreshing;
  }, [refreshing]);
  const [pickerYear, setPickerYear] = useState(() => {
    if (value.months.length > 0) return parseInt(value.months.sort()[0].split('-')[0]);
    return new Date().getFullYear();
  });
  const pickerRef = useRef<HTMLDivElement>(null);

  // Click outside to close
  useEffect(() => {
    if (!showMonthPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowMonthPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMonthPicker]);

  const handlePrevMonth = () => {
    if (value.mode === 'month') {
      const base = value.months.length === 1 ? value.months[0] : currentMonthStr;
      onChange({ ...value, months: [navigateMonth(base, -1)] });
    } else {
      // Shift date range backward by its duration
      const s = new Date(value.startDate + 'T12:00:00');
      const e = new Date(value.endDate + 'T12:00:00');
      const diff = e.getTime() - s.getTime();
      const newEnd = new Date(s.getTime() - 86400000); // day before old start
      const newStart = new Date(newEnd.getTime() - diff);
      onChange({
        ...value,
        startDate: fmt(newStart),
        endDate: fmt(newEnd),
      });
    }
  };

  const handleNextMonth = () => {
    if (value.mode === 'month') {
      const base = value.months.length === 1 ? value.months[0] : currentMonthStr;
      onChange({ ...value, months: [navigateMonth(base, 1)] });
    } else {
      const s = new Date(value.startDate + 'T12:00:00');
      const e = new Date(value.endDate + 'T12:00:00');
      const diff = e.getTime() - s.getTime();
      const newStart = new Date(e.getTime() + 86400000); // day after old end
      const newEnd = new Date(newStart.getTime() + diff);
      onChange({
        ...value,
        startDate: fmt(newStart),
        endDate: fmt(newEnd),
      });
    }
  };

  const handleMonthToggle = (monthValue: string) => {
    if (singleMonth) {
      onChange({ ...value, months: [monthValue] });
      setShowMonthPicker(false);
      return;
    }
    const next = value.months.includes(monthValue)
      ? value.months.filter(m => m !== monthValue)
      : [...value.months, monthValue];
    onChange({ ...value, months: next.sort() });
  };

  const handleModeSwitch = (mode: PeriodMode) => {
    if (mode === value.mode) return;
    if (mode === 'dateRange') {
      // Default: current month range from the selected month
      const base = value.months.length > 0 ? value.months[0] : currentMonthStr;
      const [y, m] = base.split('-').map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      onChange({
        ...value,
        mode: 'dateRange',
        startDate: `${base}-01`,
        endDate: `${base}-${String(lastDay).padStart(2, '0')}`,
      });
    } else {
      onChange({ ...value, mode: 'month' });
    }
    setShowMonthPicker(false);
  };

  const label = value.mode === 'month'
    ? formatMonthLabel(value.months)
    : formatPeriodLabel(value.startDate, value.endDate);

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-slate-600 mr-1">
        <button
          onClick={() => handleModeSwitch('month')}
          className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
            value.mode === 'month' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          Mes
        </button>
        <button
          onClick={() => handleModeSwitch('dateRange')}
          className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
            value.mode === 'dateRange' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          Periodo
        </button>
      </div>

      {value.mode === 'month' ? (
        /* ===== MONTH MODE ===== */
        <div className="relative flex items-center gap-1" ref={pickerRef}>
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button
            onClick={() => {
              setPickerYear(value.months.length > 0 ? parseInt(value.months.sort()[0].split('-')[0]) : new Date().getFullYear());
              setShowMonthPicker(!showMonthPicker);
            }}
            className="text-white text-sm font-medium min-w-[140px] text-center select-none hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
          >
            {label}
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>

          {showMonthPicker && (
            <div className="absolute top-full left-0 mt-2 z-50 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl p-4 w-[280px]">
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setPickerYear(y => y - 1)} className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <span className="text-white font-semibold text-sm">{pickerYear}</span>
                <button onClick={() => setPickerYear(y => y + 1)} className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {monthNames.map((name, i) => {
                  const mv = `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
                  const isActive = value.months.includes(mv);
                  return (
                    <button
                      key={i}
                      onClick={() => handleMonthToggle(mv)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                        isActive ? 'bg-slate-600 text-white font-semibold' : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      {name.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ===== DATE RANGE MODE ===== */
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
            title="Periodo anterior"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="flex items-center gap-1.5 bg-slate-700 rounded-lg px-2 py-1">
            <input
              type="date"
              value={value.startDate}
              onChange={e => onChange({ ...value, startDate: e.target.value })}
              className="bg-transparent text-white text-sm border-none outline-none w-[130px] [color-scheme:dark]"
            />
            <span className="text-slate-400 text-xs">ate</span>
            <input
              type="date"
              value={value.endDate}
              onChange={e => onChange({ ...value, endDate: e.target.value })}
              className="bg-transparent text-white text-sm border-none outline-none w-[130px] [color-scheme:dark]"
            />
          </div>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
            title="Proximo periodo"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      )}

      {/* "Todos os Meses" button - only in month mode */}
      {showAllMonths && value.mode === 'month' && value.months.length > 0 && !singleMonth && (
        <button
          onClick={() => onChange({ ...value, months: [] })}
          className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors text-sm"
        >
          Todos os Meses
        </button>
      )}

      {/* Atualizar button */}
      {onRefresh && (
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            title={refreshedAt ? `Atualizado às ${refreshedAt}` : 'Recarregar os dados do período'}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 disabled:cursor-wait rounded-lg text-slate-300 transition-colors flex items-center gap-2"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
          {refreshedAt && !refreshing && (
            <span className="text-[11px] text-slate-500 whitespace-nowrap">Atualizado {refreshedAt}</span>
          )}
        </div>
      )}
    </div>
  );
}

/** Helper: format Date as YYYY-MM-DD */
function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Helper: build query params from PeriodFilter for API calls */
export function periodToParams(filter: PeriodFilter): { month?: string; startDate?: string; endDate?: string } {
  if (filter.mode === 'month') {
    if (filter.months.length === 0) return {};
    return { month: filter.months.join(',') };
  }
  return {
    startDate: filter.startDate || undefined,
    endDate: filter.endDate || undefined,
  };
}
